import React from 'react';
import { CheckCircle2, Circle, AlertCircle } from 'lucide-react';

interface TechnicalSkill {
  name: string;
  proficiency: number;
  category: string;
  yearsExperience?: number;
  lastUsed?: string;
}

interface TechnicalSkillsMatrixProps {
  skills: TechnicalSkill[];
  title?: string;
}

const TechnicalSkillsMatrix: React.FC<TechnicalSkillsMatrixProps> = ({
  skills,
  title = 'Technical Skills Matrix'
}) => {
  const categorizedSkills = skills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {} as Record<string, TechnicalSkill[]>);

  const getProficiencyLevel = (score: number) => {
    if (score >= 85) return { label: 'Expert', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50', icon: CheckCircle2 };
    if (score >= 70) return { label: 'Advanced', color: 'bg-cyan-600', textColor: 'text-cyan-700', bgLight: 'bg-cyan-50', icon: CheckCircle2 };
    if (score >= 50) return { label: 'Intermediate', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50', icon: Circle };
    return { label: 'Developing', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50', icon: AlertCircle };
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600 mt-1">Comprehensive assessment of technical capabilities across key domains</p>
      </div>

      <div className="p-6 space-y-6">
        {Object.entries(categorizedSkills).map(([category, categorySkills]) => (
          <div key={category} className="space-y-3">
            <h4 className="text-md font-semibold text-gray-800 flex items-center gap-2 pb-2 border-b border-gray-200">
              <span className="inline-block w-1 h-5 bg-blue-500 rounded"></span>
              {category}
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {categorySkills.sort((a, b) => b.proficiency - a.proficiency).map((skill, index) => {
                const level = getProficiencyLevel(skill.proficiency);
                const Icon = level.icon;

                return (
                  <div
                    key={index}
                    className={`${level.bgLight} border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${level.textColor}`} />
                        <h5 className="font-medium text-gray-900">{skill.name}</h5>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${level.color} text-white`}>
                        {skill.proficiency}%
                      </span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                      <div
                        className={`h-2 rounded-full ${level.color} transition-all duration-500`}
                        style={{ width: `${skill.proficiency}%` }}
                      />
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span className={`font-medium ${level.textColor}`}>{level.label}</span>
                      {skill.yearsExperience && (
                        <span>{skill.yearsExperience}+ years</span>
                      )}
                      {skill.lastUsed && (
                        <span>Last: {skill.lastUsed}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>Expert (85-100%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-cyan-600"></div>
            <span>Advanced (70-84%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span>Intermediate (50-69%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>Developing (&lt;50%)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalSkillsMatrix;
